import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, ScanEye, LineChart, LayoutDashboard, MessageSquareQuote, Users, Link as LinkIcon, ShieldCheck, Bell, Code, Mail } from 'lucide-react';
import { ProviderChip } from '@/components/ProviderChip';
import PricingGrid from '@/components/PricingGrid';
import { api } from '@/lib/api';
import { Link } from 'react-router-dom';

function FeatureCard({ icon: Icon, title, description }) {
  return (
    <div className="rounded-[var(--radius)] border bg-card p-5 card-elevated card-elevated-hover transition-shadow duration-200">
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--accent)/0.12)] text-[hsl(var(--accent))]">
        <Icon className="h-5 w-5" />
      </div>
      <div className="mt-3 font-display text-base font-semibold">{title}</div>
      <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

function StepCard({ n, title, description }) {
  return (
    <div className="rounded-[var(--radius)] border bg-card p-5">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-semibold">{n}</div>
      <div className="mt-3 font-display text-base font-semibold">{title}</div>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get('/billing/plans').then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="sticky top-0 z-30 border-b bg-background/85 backdrop-blur">
        <div className="mx-auto flex max-w-[1240px] items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"><ScanEye className="h-5 w-5" /></div>
            <div>
              <div className="font-display text-base font-semibold">GEOrush</div>
              <div className="text-[11px] text-muted-foreground -mt-0.5">AEO Visibility</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm">
            <a href="#features" className="text-muted-foreground hover:text-foreground">Features</a>
            <a href="#how-it-works" className="text-muted-foreground hover:text-foreground">How it works</a>
            <a href="#providers" className="text-muted-foreground hover:text-foreground">Providers</a>
            <Link to="/pricing" className="text-muted-foreground hover:text-foreground" data-testid="landing-nav-pricing-link">Pricing</Link>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="landing-login-button">Sign in</Button>
            <Button size="sm" onClick={() => navigate('/register')} data-testid="landing-signup-button">Get started</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-mist pointer-events-none" />
        <div className="relative mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 max-w-[600px]">
            <div className="inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))]" />
              Live tracking across ChatGPT, Claude & Gemini
            </div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              See how AI answers <span className="text-[hsl(var(--accent))]">talk about your brand</span>.
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
              GEOrush runs your prompts across leading AI assistants and shows where you’re cited,
              where you’re invisible, and how you stack up against competitors — in one calm dashboard.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={() => navigate('/register')} data-testid="landing-hero-cta-button">
                Get started <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/login')}>
                Sign in
              </Button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground" data-testid="landing-hero-trial-note">
              7-day free trial on every paid plan · Cancel anytime
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <ProviderChip providerKey="openai" active />
              <ProviderChip providerKey="anthropic" active />
              <ProviderChip providerKey="gemini" active />
              <ProviderChip providerKey="perplexity" comingSoon />
              <ProviderChip providerKey="copilot" comingSoon />
              <ProviderChip providerKey="google_ai_overview" comingSoon />
            </div>
          </div>

          {/* Dashboard mockup */}
          <div className="lg:col-span-6">
            <div className="relative rounded-[calc(var(--radius)+4px)] border bg-card p-4 sm:p-5 card-elevated">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-rose-400" />
                  <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                  <div className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                </div>
                <div className="text-xs text-muted-foreground">ageorush.io/dashboard</div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {[['Mention rate', '68%'], ['Share of voice', '41%'], ['Avg sentiment', '+0.42']].map(([l, v]) => (
                  <div key={l} className="rounded-lg border bg-secondary/40 p-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</div>
                    <div className="mt-1 font-display text-lg font-semibold tabular">{v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">Mentions over time</div>
                  <div className="text-xs text-muted-foreground">Last 30 days</div>
                </div>
                <svg viewBox="0 0 400 100" className="mt-3 w-full h-24">
                  <defs>
                    <linearGradient id="lg1" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="hsl(186 78% 34%)" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="hsl(186 78% 34%)" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0,80 L40,70 L80,72 L120,55 L160,60 L200,40 L240,45 L280,32 L320,38 L360,20 L400,25 L400,100 L0,100 Z" fill="url(#lg1)" />
                  <path d="M0,80 L40,70 L80,72 L120,55 L160,60 L200,40 L240,45 L280,32 L320,38 L360,20 L400,25" fill="none" stroke="hsl(186 78% 34%)" strokeWidth="2" />
                </svg>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {['ChatGPT', 'Claude', 'Gemini'].map((p) => (
                  <div key={p} className="rounded-lg border p-3">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{p}</div>
                    <div className="mt-1 font-display text-base font-semibold tabular">
                      {p === 'ChatGPT' ? '72%' : p === 'Claude' ? '61%' : '78%'}
                    </div>
                    <div className="mt-2 h-1.5 rounded-full bg-secondary">
                      <div
                        className="h-1.5 rounded-full bg-[hsl(var(--accent))]"
                        style={{ width: p === 'ChatGPT' ? '72%' : p === 'Claude' ? '61%' : '78%' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-[hsl(var(--accent))] font-medium">The AI visibility stack</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Everything you need to win Answer Engine Optimization.</h2>
          <p className="mt-3 text-muted-foreground">Track brand mentions, sentiment, competitor share and citations across leading AI assistants — with drill-downs into the exact answers users see.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <FeatureCard icon={MessageSquareQuote} title="Prompt-based tracking" description="Run manual prompts or generate them with AI — all evaluated across ChatGPT, Claude and Gemini." />
          <FeatureCard icon={LineChart} title="Historical trends" description="Watch mention rate and sentiment evolve over time as your AEO efforts land." />
          <FeatureCard icon={LayoutDashboard} title="Provider comparison" description="Compare visibility side-by-side across each AI provider — spot who’s citing you most." />
          <FeatureCard icon={Users} title="Competitor share of voice" description="See exactly how you rank vs your competitors in AI answers." />
          <FeatureCard icon={LinkIcon} title="Citation intelligence" description="Discover which domains and URLs the AIs cite when discussing your industry." />
          <FeatureCard icon={ShieldCheck} title="Sentiment analysis" description="Understand not just where you appear, but how you’re portrayed." />
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="bg-secondary/40 border-y">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--accent))] font-medium">How it works</div>
            <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight">Set up in minutes, get answers in seconds.</h2>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            <StepCard n="1" title="Add your brand" description="Create a project with your brand, industry, keywords and competitors." />
            <StepCard n="2" title="Add or generate prompts" description="Write prompts yourself or let AI generate high-intent queries for your industry." />
            <StepCard n="3" title="Run a scan & explore" description="We query each AI, analyze the answers, and give you rich dashboards + drill-downs." />
          </div>
        </div>
      </section>

      {/* Providers */}
      <section id="providers" className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--accent))] font-medium">Providers</div>
            <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight">Every major AI answer engine, one dashboard.</h2>
            <p className="mt-3 text-muted-foreground">We’re live with ChatGPT, Claude and Gemini today. Perplexity, Copilot and Google AI Overview are on the roadmap.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              <ProviderChip providerKey="openai" active />
              <ProviderChip providerKey="anthropic" active />
              <ProviderChip providerKey="gemini" active />
              <ProviderChip providerKey="perplexity" comingSoon />
              <ProviderChip providerKey="copilot" comingSoon />
              <ProviderChip providerKey="google_ai_overview" comingSoon />
            </div>
          </div>
          <div className="rounded-[var(--radius)] border bg-card p-6 card-elevated">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Providers queried per scan</div>
                <div className="mt-1 font-display text-3xl font-semibold tabular">3+</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Signals extracted</div>
                <div className="mt-1 font-display text-3xl font-semibold tabular">6</div>
              </div>
              <div className="col-span-2 border-t pt-4">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Signals</div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  {['Brand mentions', 'Position', 'Sentiment', 'Competitor share', 'Citations', 'Trend'].map((s) => (
                    <span key={s} className="rounded-full border bg-secondary/60 px-2.5 py-1">{s}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing snapshot */}
      <section id="pricing" className="bg-secondary/40 border-y">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--accent))] font-medium">Pricing</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Start small. Scale when you&apos;re ready.</h2>
            <p className="mt-3 text-muted-foreground">Every paid plan starts with a 7-day free trial. Cancel anytime. Payments powered by Dodo Payments.</p>
          </div>
          <div className="mt-10">
            {plans.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-72 rounded-[var(--radius)] bg-card animate-pulse" />)}</div>
            ) : (
              <PricingGrid plans={plans} />
            )}
          </div>
          <div className="mt-8 text-center">
            <Link to="/pricing" className="text-sm text-[hsl(var(--accent))] font-medium hover:underline inline-flex items-center gap-1" data-testid="landing-see-full-pricing-link">
              See full plan comparison & FAQ <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 py-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Ready to see where you show up?</h3>
            <p className="mt-2 text-sm opacity-80">Create your first brand project in under 60 seconds.</p>
          </div>
          <div className="flex gap-3">
            <Button size="lg" variant="secondary" onClick={() => navigate('/register')} data-testid="landing-cta-band-button">
              Get started <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 py-8 text-xs text-muted-foreground flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>© {new Date().getFullYear()} GEOrush — AEO Visibility & AI Citation Tracking</div>
        <div className="flex items-center gap-3 flex-wrap">
          <a
            href="mailto:support.georush@gmail.com"
            className="inline-flex items-center gap-1.5 hover:text-[hsl(var(--accent))] transition-colors"
            data-testid="footer-support-email"
          >
            <Mail className="h-3.5 w-3.5" /> support.georush@gmail.com
          </a>
          <span className="hidden md:inline text-border">·</span>
          <Code className="h-3.5 w-3.5" /> Built with FastAPI + React
          <Bell className="h-3.5 w-3.5 ml-2" /> Alerts — coming soon
        </div>
      </footer>
    </div>
  );
}
